import { type TableStateControl, useTableSettings } from '../../hooks';
import type { BulkActionOverlayAction } from '../BulkActionOverlay';
import {
  type ServerSideSelectionState,
  type TableBaseProps,
  type TableExportParams,
  TableShell,
} from '../TableShell/TableShell';

export type { ServerSideSelectionState, TableBaseProps, TableExportParams };

export type TableData<TData> = {
  data: TData[];
  rowCount: number;
  isLoading: boolean;
  isFetching?: boolean;
  error?: Error | null;
};

export type ServerTableProps<TData> = TableBaseProps<TData> & {
  /**
   * Table data object containing the data array, row count, loading state, and optional error.
   * This is managed by the parent via TanStack Query.
   */
  data: TableData<TData>;
  /**
   * Table state control object (in ServerTable, it is always from useTableUrlState)
   */
  stateControl: TableStateControl;
  /**
   * Called when selection changes. Use for bulk actions (e.g. bulk update).
   * - mode 'partial': apply to exactly the given selectedIds.
   * - mode 'all': apply to all rows matching current filters/sort, then exclude excludedIds (backend needs current query context).
   */
  onSelectionChange?: (selection: ServerSideSelectionState) => void;
  /**
   * Initial selection state (e.g. from a store). Use when the table can unmount (e.g. view switch)
   * so selection is restored when the table mounts again.
   */
  initialSelectionState?: ServerSideSelectionState;
  /** When provided and row selection is enabled, shows BulkActionOverlay when rows are selected. */
  bulkActions?: BulkActionOverlayAction[];
  /** "Export Visible": called with the current page and the column layout. */
  onExportVisible?: (params: TableExportParams<TData>) => void;
  onExportAll?: () => void;
};

/**
 * ServerTable shows rows the API has already filtered, searched, sorted and paged: fetch each page from the state in
 * `stateControl` and pass it with the total `rowCount`. The pager is always on.
 * For rows held in memory, use {@link ClientTable}; both share the same toolbar, settings, selection and grid.
 *
 * Row checkboxes are on by default (`enableRowSelection`); "select all" covers every matching row across pages and is
 * reported as `mode: 'all'` with the rows the user unticked, so the API can apply bulk actions to the whole result set.
 *
 * @example
 * ```tsx
 * function DRF() {
 *   const stateControl = useTableUrlState();
 *   const { sorting, filters, pagination, globalFilter } = stateControl;
 *
 *   const { data, isLoading, error } = useQuery({
 *     queryKey: ['projects', sorting, filters, pagination, globalFilter],
 *     queryFn: async () => {
 *       const response = await fetch('/api/projects', {
 *         method: 'POST',
 *         body: JSON.stringify({ sorting, filters, pagination, globalFilter }),
 *       });
 *       return response.json();
 *     },
 *   });
 *
 *   return (
 *     <ServerTable
 *       id="projects-table"
 *       columns={columns}
 *       data={{
 *         data: data?.results ?? [],
 *         rowCount: data?.totalCount ?? 0,
 *         isLoading,
 *         error: error ?? null,
 *       }}
 *       stateControl={stateControl}
 *     />
 *   );
 * }
 * ```
 */
function ServerTable<TData>({ data, ...props }: ServerTableProps<TData>) {
  const settings = useTableSettings({ id: props.id });

  // The API pages the rows, so the pager is always shown.
  return <TableShell {...props} data={data.data} rowCount={data.rowCount} settings={settings} enablePagination />;
}

export { ServerTable };
